import React, { useState, useEffect, useRef } from 'react';
import {
  Search, Plus, Send, Phone, Mail, Globe, User, Tag, ArrowLeft,
  FileText, Trash2, Edit3, Check, X, RefreshCw, MessageSquare,
  Bot, Clock, MoreVertical, Smartphone, ChevronRight, CheckCheck,
  Pause, Play, AlertCircle
} from 'lucide-react';
import {
  getChatContacts,
  createChatContact,
  updateChatContact,
  deleteChatContact,
  getContactMessages,
  sendContactMessage,
  WhatsAppUserContact,
  ChatMessage
} from '../services/chatsApi';
import { getSocket } from '../services/api';
import { ConfirmModal } from './ConfirmModal';
import { useSession } from '../context/SessionContext';

interface ChatDashboardProps {
  currentUser?: any;
  toast?: {
    success: (msg: string) => void;
    error: (msg: string) => void;
    warning: (msg: string) => void;
    info: (msg: string) => void;
  };
}

const getMessageIdentity = (message: ChatMessage) => {
  if (message.id > 0) return `db:${message.id}`;
  if (message.whatsapp_message_id) return `whatsapp:${message.whatsapp_message_id}`;
  return [
    message.contact_id,
    message.session_id,
    message.direction,
    message.created_at,
    message.content
  ].join(':');
};

const appendMessageIfNew = (messages: ChatMessage[], message: ChatMessage) => {
  const identity = getMessageIdentity(message);
  return messages.some(existing => getMessageIdentity(existing) === identity)
    ? messages
    : [...messages, message];
};

const uniqueMessages = (messages: ChatMessage[]) => {
  const seen = new Set<string>();
  return messages.filter(message => {
    const identity = getMessageIdentity(message);
    if (seen.has(identity)) return false;
    seen.add(identity);
    return true;
  });
};

export const ChatDashboard: React.FC<ChatDashboardProps> = ({ currentUser, toast }) => {
  const { sessions, selectedSessionId, setSelectedSessionId } = useSession();

  // Estados de datos
  const [contacts, setContacts] = useState<WhatsAppUserContact[]>([]);
  const [selectedContact, setSelectedContact] = useState<WhatsAppUserContact | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Mensajería en vivo
  const [inputText, setInputText] = useState('');
  const [isSending, setIsSending] = useState(false);

  // Edición del Contacto
  const [isEditingContact, setIsEditingContact] = useState(false);
  const [editForm, setEditForm] = useState<Partial<WhatsAppUserContact>>({});
  const [isSavingContact, setIsSavingContact] = useState(false);

  // Modal Nuevo Contacto
  const [showNewContactModal, setShowNewContactModal] = useState(false);
  const [newContactForm, setNewContactForm] = useState({
    phone: '',
    name: '',
    email: '',
    country_code: '',
    country_name: '',
    notes: '',
    tags: ''
  });
  const [isCreatingContact, setIsCreatingContact] = useState(false);

  // Modal Eliminar Contacto
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeletingContact, setIsDeletingContact] = useState(false);
  const [isMobileChatOpen, setIsMobileChatOpen] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Auto scroll hacia el último mensaje
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Cargar contactos al iniciar
  useEffect(() => {
    loadContacts();
  }, []);

  // Escuchar mensajes entrantes en tiempo real por WebSockets
  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;

    const handleMessageReceived = (data: any) => {
      if (data && data.message) {
        const incomingMsg: ChatMessage = data.message;
        // Si el mensaje pertenece al contacto actualmente abierto, añadirlo a la vista
        if (selectedContact && incomingMsg.contact_id === selectedContact.id) {
          setMessages(prev => appendMessageIfNew(prev, incomingMsg));
        }
        // Actualizar último mensaje en la lista de contactos
        setContacts(prev => prev.map(c => {
          if (c.id === incomingMsg.contact_id) {
            return {
              ...c,
              last_message: incomingMsg.content,
              last_interaction_at: incomingMsg.created_at,
              last_session_id: incomingMsg.session_id
            };
          }
          return c;
        }));
      }
    };

    socket.on('chat_message_received', handleMessageReceived);

    return () => {
      socket.off('chat_message_received', handleMessageReceived);
    };
  }, [selectedContact]);

  const loadContacts = async () => {
    try {
      setLoadingContacts(true);
      const res = await getChatContacts(searchQuery);
      if (res.success && Array.isArray(res.contacts)) {
        setContacts(res.contacts);
        // Mantener seleccionado o seleccionar el primero si no hay ninguno
        if (!selectedContact && res.contacts.length > 0) {
          // En móvil iniciamos mostrando la bandeja para que el usuario
          // pueda elegir la conversación; en escritorio ambas columnas son visibles.
          handleSelectContact(res.contacts[0], false);
        } else if (selectedContact) {
          const updated = res.contacts.find(c => c.id === selectedContact.id);
          if (updated) setSelectedContact(updated);
        }
      }
    } catch (e: any) {
      console.error(e);
      toast?.error('Error al cargar contactos');
    } finally {
      setLoadingContacts(false);
    }
  };

  const handleSelectContact = async (contact: WhatsAppUserContact, openMobile = true) => {
    setIsMobileChatOpen(openMobile);
    setSelectedContact(contact);
    setIsEditingContact(false);
    setEditForm(contact);
    try {
      setLoadingMessages(true);
      const res = await getContactMessages(contact.id);
      // La conversación se muestra únicamente con mensajes persistidos y
      // asociados al contact_id real. No fabricar un mensaje desde
      // last_message evita atribuir una respuesta del bot al cliente.
      setMessages(res.success && Array.isArray(res.messages) ? uniqueMessages(res.messages) : []);
    } catch (e) {
      console.error(e);
      setMessages([]);
    } finally {
      setLoadingMessages(false);
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputText.trim() || !selectedContact || isSending) return;

    const textToSend = inputText.trim();
    setInputText('');

    try {
      setIsSending(true);
      const res = await sendContactMessage(selectedContact.id, textToSend, selectedSessionId || undefined);
      if (res.success && res.message) {
        // El backend también emite el mismo mensaje por WebSocket. Ambos
        // caminos convergen aquí, pero el mensaje se muestra una sola vez
        // usando el ID generado por PostgreSQL/WhatsApp.
        setMessages(prev => appendMessageIfNew(prev, res.message!));
        // Actualizar último mensaje en la lista
        setContacts(prev => prev.map(c => {
          if (c.id === selectedContact.id) {
            return {
              ...c,
              last_message: textToSend,
              last_interaction_at: new Date().toISOString(),
              last_session_id: selectedSessionId || c.last_session_id
            };
          }
          return c;
        }));
      } else {
        toast?.error(res.error || 'Error al enviar mensaje');
        setInputText(textToSend); // restaurar texto si falló
      }
    } catch (e: any) {
      toast?.error(e?.message || 'Error al enviar mensaje');
      setInputText(textToSend);
    } finally {
      setIsSending(false);
    }
  };

  const handleSaveContactDetails = async () => {
    if (!selectedContact) return;
    try {
      setIsSavingContact(true);
      const res = await updateChatContact(selectedContact.id, editForm);
      if (res.success) {
        toast?.success('Detalles del contacto actualizados');
        const updated = { ...selectedContact, ...editForm } as WhatsAppUserContact;
        setSelectedContact(updated);
        setContacts(prev => prev.map(c => c.id === updated.id ? updated : c));
        setIsEditingContact(false);
      } else {
        toast?.error(res.error || 'Error al guardar contacto');
      }
    } catch (e: any) {
      toast?.error(e?.message || 'Error guardando contacto');
    } finally {
      setIsSavingContact(false);
    }
  };

  const handleCreateContact = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newContactForm.phone.trim()) {
      toast?.warning('El número de teléfono es obligatorio');
      return;
    }

    try {
      setIsCreatingContact(true);
      const tagsArray = newContactForm.tags
        ? newContactForm.tags.split(',').map(t => t.trim()).filter(Boolean)
        : [];

      const res = await createChatContact({
        phone: newContactForm.phone,
        name: newContactForm.name,
        email: newContactForm.email,
        country_code: newContactForm.country_code,
        country_name: newContactForm.country_name,
        notes: newContactForm.notes,
        tags: tagsArray
      });

      if (res.success && res.contact) {
        toast?.success('Contacto creado exitosamente');
        setShowNewContactModal(false);
        setNewContactForm({
          phone: '',
          name: '',
          email: '',
          country_code: '',
          country_name: '',
          notes: '',
          tags: ''
        });
        await loadContacts();
        handleSelectContact(res.contact);
      } else {
        toast?.error(res.error || 'Error al crear contacto');
      }
    } catch (e: any) {
      toast?.error(e?.message || 'Error al crear contacto');
    } finally {
      setIsCreatingContact(false);
    }
  };

  const handleDeleteContact = async () => {
    if (!selectedContact) return;
    try {
      setIsDeletingContact(true);
      const res = await deleteChatContact(selectedContact.id);
      if (res.success) {
        toast?.success('Contacto e historial eliminados');
        setShowDeleteModal(false);
        const remaining = contacts.filter(c => c.id !== selectedContact.id);
        setContacts(remaining);
        if (remaining.length > 0) {
          handleSelectContact(remaining[0]);
        } else {
          setSelectedContact(null);
          setMessages([]);
        }
      } else {
        toast?.error(res.error || 'Error al eliminar contacto');
      }
    } catch (e: any) {
      toast?.error(e?.message || 'Error al eliminar contacto');
    } finally {
      setIsDeletingContact(false);
    }
  };

  const getCountryFlagEmoji = (countryCode: string) => {
    switch (countryCode) {
      case '51': return '🇵🇪';
      case '52': return '🇲🇽';
      case '57': return '🇨🇴';
      case '54': return '🇦🇷';
      case '56': return '🇨🇱';
      case '593': return '🇪🇨';
      case '591': return '🇧🇴';
      case '34': return '🇪🇸';
      case '1': return '🇺🇸';
      default: return '🌎';
    }
  };

  const formatSessionPhone = (phoneNumber?: string | null) => {
    const digits = phoneNumber?.replace(/\D/g, '');
    return digits ? `+${digits}` : '';
  };

  const getSessionLabel = (sessionId?: string | null) => {
    if (!sessionId) return 'Sesión no identificada';
    const session = sessions.find(s => s.sessionId === sessionId);
    const phone = formatSessionPhone(session?.phoneNumber);
    return phone || `Sesión ${sessionId.slice(0, 8)}`;
  };

  const activeSessions = sessions.filter(session => session.isReady || session.status === 'connected');
  const conversationSessionIds = Array.from(
    new Set(messages.map(message => message.session_id).filter(Boolean))
  );
  const selectedSessionLabel = getSessionLabel(selectedSessionId);

  return (
    <div className="relative h-[calc(100dvh-15rem)] min-h-[22rem] w-full min-w-0 max-w-full flex flex-col bg-theme-base rounded-xl sm:rounded-2xl border border-theme overflow-hidden shadow-2xl sm:h-[calc(100dvh-13rem)] lg:h-[calc(100dvh-12rem)]">
      {/* Dashboard Main Grid: 3 Columnas Estilo WhatsApp Web Profesional */}
      <div className="flex-1 min-h-0 min-w-0 flex overflow-hidden">
        
        {/* ========================================================
            COLUMNA 1: LISTA DE CHATS & CLIENTES (IZQUIERDA)
            ======================================================== */}
        <div className={`${isMobileChatOpen ? 'hidden md:flex' : 'flex'} w-full min-h-0 min-w-0 md:w-72 lg:w-80 xl:w-96 border-r border-theme bg-theme-card flex-col flex-shrink-0`}>
          {/* Header de la Columna */}
          <div className="p-3 sm:p-4 border-b border-theme flex items-center justify-between gap-2 bg-theme-base/20">
            <div className="flex min-w-0 items-center gap-2">
              <div className="p-2 bg-primary-500/10 text-primary-500 rounded-xl">
                <MessageSquare size={18} />
              </div>
              <h2 className="truncate font-bold text-sm text-theme-main">Chats de WhatsApp</h2>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={loadContacts}
                className="p-2 text-theme-muted hover:text-theme-main hover:bg-theme-base rounded-lg transition-colors"
                title="Refrescar lista"
              >
                <RefreshCw size={15} className={loadingContacts ? 'animate-spin' : ''} />
              </button>
              <button
                onClick={() => setShowNewContactModal(true)}
                className="p-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors flex items-center gap-1 text-xs font-bold shadow-sm"
                title="Registrar Nuevo Contacto"
              >
                <Plus size={15} />
                <span className="hidden sm:inline">Nuevo</span>
              </button>
            </div>
          </div>

          {/* Buscador */}
          <div className="p-3 border-b border-theme bg-theme-base/10">
            <div className="relative">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-theme-muted" />
              <input
                type="text"
                placeholder="Buscar por nombre, teléfono o email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') loadContacts();
                }}
                className="w-full pl-9 pr-3 py-2 bg-theme-base border border-theme rounded-xl text-xs text-theme-main placeholder-theme-muted focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>

          {/* Lista scrolleable de contactos */}
          <div className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto divide-y divide-theme/60">
            {loadingContacts && contacts.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-theme-muted text-xs gap-2">
                <RefreshCw size={20} className="animate-spin text-primary-500" />
                <span>Cargando conversaciones...</span>
              </div>
            ) : contacts.length === 0 ? (
              <div className="text-center py-16 px-4 text-theme-muted text-xs">
                <MessageSquare size={32} className="mx-auto mb-2 opacity-30" />
                <p className="font-semibold text-theme-main mb-1">No hay conversaciones</p>
                <p>Las interacciones entrantes de WhatsApp o contactos creados aparecerán aquí.</p>
              </div>
            ) : (
              contacts.map((contact) => {
                const isSelected = selectedContact?.id === contact.id;
                const flag = getCountryFlagEmoji(contact.country_code);

                return (
                  <button
                    key={contact.id}
                    onClick={() => handleSelectContact(contact)}
                    className={`w-full text-left p-3.5 flex items-start gap-3 transition-colors ${
                      isSelected
                        ? 'bg-primary-500/10 border-r-4 border-primary-500'
                        : 'hover:bg-theme-base/40'
                    }`}
                  >
                    {/* Avatar con bandera */}
                    <div className="relative flex-shrink-0">
                      <div className="w-11 h-11 rounded-full bg-gradient-to-tr from-primary-600 to-indigo-600 text-white flex items-center justify-center font-bold text-sm shadow-md">
                        {contact.name ? contact.name.charAt(0).toUpperCase() : <User size={18} />}
                      </div>
                      <span className="absolute -bottom-1 -right-1 text-sm bg-theme-card rounded-full shadow-sm p-0.5" title={contact.country_name || 'País'}>
                        {flag}
                      </span>
                    </div>

                    {/* Datos del contacto y último mensaje */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-1 mb-1">
                        <h4 className="font-bold text-xs text-theme-main truncate">
                          {contact.name || `+${contact.phone}`}
                        </h4>
                        <span className="text-[10px] text-theme-muted flex-shrink-0">
                          {contact.last_interaction_at
                            ? new Date(contact.last_interaction_at).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })
                            : ''}
                        </span>
                      </div>

                      <div className="flex items-center justify-between gap-2">
                        <p className="text-[11px] text-theme-muted truncate">
                          {contact.last_message || `+${contact.phone}`}
                        </p>
                        {contact.email && (
                          <span className="text-[9px] bg-theme-base px-1.5 py-0.5 rounded text-theme-muted font-mono truncate max-w-[80px]">
                            {contact.email}
                          </span>
                        )}
                      </div>

                      {contact.last_session_id && (
                        <div className="mt-1.5 flex items-center gap-1 text-[9px] text-primary-600 dark:text-primary-400 font-medium">
                          <Smartphone size={10} />
                          <span className="truncate">Última sesión: {getSessionLabel(contact.last_session_id)}</span>
                        </div>
                      )}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* ========================================================
            COLUMNA 2: CHAT CON EL CONTACTO (CENTRO - WHATSAPP WEB)
            ======================================================== */}
        <div className={`${isMobileChatOpen ? 'flex' : 'hidden md:flex'} relative min-h-0 min-w-0 w-full flex-1 flex-col bg-theme-base/30`}>
          {selectedContact ? (
            <>
              {/* Header de conversación activa */}
              <div className="p-2.5 sm:p-3.5 lg:px-6 border-b border-theme bg-theme-card flex flex-col lg:flex-row lg:items-center lg:justify-between gap-2 sm:gap-3 shadow-sm z-10">
                <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                  <button
                    onClick={() => setIsMobileChatOpen(false)}
                    className="md:hidden flex-shrink-0 p-2 -ml-1 rounded-xl text-theme-muted hover:bg-theme-base hover:text-theme-main transition-colors"
                    aria-label="Volver a la lista de chats"
                    title="Volver a chats"
                  >
                    <ArrowLeft size={18} />
                  </button>
                  <div className="relative">
                    <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-gradient-to-tr from-primary-600 to-indigo-600 text-white flex items-center justify-center font-bold text-sm">
                      {selectedContact.name ? selectedContact.name.charAt(0).toUpperCase() : <User size={16} />}
                    </div>
                    <span className="absolute -bottom-1 -right-1 text-xs">
                      {getCountryFlagEmoji(selectedContact.country_code)}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-extrabold text-sm text-theme-main flex items-center gap-2 min-w-0">
                      <span className="truncate">
                      {selectedContact.name || `+${selectedContact.phone}`}
                      </span>
                      {selectedContact.country_name && (
                        <span className="hidden sm:inline text-[10px] font-normal text-theme-muted flex-shrink-0">
                          ({selectedContact.country_name})
                        </span>
                      )}
                    </h3>
                    <p className="max-w-full text-[10px] sm:text-[11px] text-primary-600 dark:text-primary-400 font-mono font-medium truncate">
                      +{selectedContact.phone}
                      {selectedContact.email && ` • ${selectedContact.email}`}
                    </p>
                    <div className="mt-1 flex flex-wrap items-center gap-1.5">
                      <span className="inline-flex items-center gap-1 rounded-full bg-theme-base px-2 py-0.5 text-[9px] font-semibold text-theme-muted">
                        <Smartphone size={10} />
                        {conversationSessionIds.length > 1
                          ? `Historial combinado · ${conversationSessionIds.length} sesiones`
                          : conversationSessionIds.length === 1
                            ? `Historial: ${getSessionLabel(conversationSessionIds[0])}`
                            : 'Sin mensajes persistidos'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex w-full min-w-0 items-center justify-between gap-2 lg:w-auto lg:justify-end">
                  <div className="flex min-w-0 flex-1 items-center gap-2 rounded-xl border border-primary-500/20 bg-primary-500/5 px-2.5 py-2 lg:hidden">
                    <Smartphone size={14} className="flex-shrink-0 text-primary-600 dark:text-primary-400" />
                    <div className="min-w-0 flex-1">
                      <p className="text-[9px] font-bold uppercase tracking-[0.1em] text-theme-muted">Enviando desde</p>
                      <select
                        value={selectedSessionId || ''}
                        onChange={(e) => setSelectedSessionId(e.target.value || null)}
                        className="w-full max-w-full cursor-pointer bg-transparent text-[11px] font-bold text-primary-700 outline-none dark:text-primary-300"
                        title="Selecciona la sesión que enviará este mensaje"
                      >
                        {activeSessions.length === 0 ? (
                          <option value="">Sin sesión activa</option>
                        ) : (
                          activeSessions.map(session => (
                            <option key={session.sessionId} value={session.sessionId}>
                              {formatSessionPhone(session.phoneNumber) || `Sesión ${session.sessionId.slice(0, 8)}`}
                            </option>
                          ))
                        )}
                      </select>
                    </div>
                    <span
                      className={`h-2 w-2 flex-shrink-0 rounded-full ${selectedSessionId && activeSessions.some(session => session.sessionId === selectedSessionId) ? 'bg-emerald-500' : 'bg-amber-400'}`}
                      title={selectedSessionId ? selectedSessionLabel : 'Sin sesión seleccionada'}
                    />
                  </div>
                  <div className="hidden min-w-0 items-center gap-2 rounded-xl border border-primary-500/20 bg-primary-500/5 px-3 py-2 lg:flex">
                    <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary-500/15 text-primary-600 dark:text-primary-400">
                      <Smartphone size={14} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[9px] font-bold uppercase tracking-[0.12em] text-theme-muted">Escribiendo desde</p>
                      <select
                        value={selectedSessionId || ''}
                        onChange={(e) => setSelectedSessionId(e.target.value || null)}
                        className="max-w-[150px] min-w-0 cursor-pointer bg-transparent text-[11px] font-bold text-primary-700 outline-none dark:text-primary-300"
                        title="Selecciona la sesión de WhatsApp que enviará este mensaje"
                      >
                        {activeSessions.length === 0 ? (
                          <option value="">Sin sesión activa</option>
                        ) : (
                          activeSessions.map(session => (
                            <option key={session.sessionId} value={session.sessionId}>
                              {formatSessionPhone(session.phoneNumber) || `Sesión ${session.sessionId.slice(0, 8)}`}
                            </option>
                          ))
                        )}
                      </select>
                    </div>
                    <span
                      className={`h-2 w-2 rounded-full ${selectedSessionId && activeSessions.some(session => session.sessionId === selectedSessionId) ? 'bg-emerald-500 shadow-[0_0_0_3px_rgba(16,185,129,0.15)]' : 'bg-amber-400'}`}
                      title={selectedSessionId ? selectedSessionLabel : 'Sin sesión seleccionada'}
                    />
                  </div>
                  <button
                    onClick={() => setIsEditingContact(!isEditingContact)}
                    className="shrink-0 p-2 text-theme-muted hover:text-theme-main hover:bg-theme-base rounded-xl transition-colors flex items-center gap-1.5 text-xs font-semibold border border-theme"
                    title="Ver/Editar Ficha del Usuario"
                  >
                    <Edit3 size={14} />
                    <span className="hidden xl:inline">Perfil / Datos</span>
                  </button>
                </div>
              </div>

              {/* Mensajes - Ventana interactiva */}
              <div className="min-h-0 min-w-0 flex-1 overflow-x-hidden overflow-y-auto p-3 sm:p-4 lg:p-6 space-y-3 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] dark:bg-[radial-gradient(#1f2937_1px,transparent_1px)] [background-size:16px_16px]">
                {loadingMessages ? (
                  <div className="flex flex-col items-center justify-center h-full text-theme-muted text-xs gap-2">
                    <RefreshCw size={22} className="animate-spin text-primary-500" />
                    <span>Cargando historial de mensajes...</span>
                  </div>
                ) : messages.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-theme-muted text-xs gap-2">
                    <MessageSquare size={36} className="opacity-20 text-primary-500" />
                    <p className="font-semibold text-theme-main">Aún no hay mensajes en este chat</p>
                    <p>Escribe un mensaje abajo para interactuar con este cliente a través de WhatsApp.</p>
                  </div>
                ) : (
                  messages.map((msg, idx) => {
                    const isOutgoing = msg.direction === 'outgoing';
                    const isBot = msg.sender_type === 'bot';
                    const isAgent = msg.sender_type === 'agent';

                    return (
                      <div
                        key={msg.id || idx}
                        className={`flex flex-col ${isOutgoing ? 'items-end' : 'items-start'}`}
                      >
                        <div
                            className={`min-w-0 max-w-[92%] sm:max-w-[82%] lg:max-w-[72%] [overflow-wrap:anywhere] rounded-2xl px-3.5 sm:px-4 py-2.5 shadow-sm text-xs leading-relaxed relative ${
                            isOutgoing
                              ? 'bg-primary-600 text-white rounded-tr-none'
                              : 'bg-theme-card text-theme-main border border-theme rounded-tl-none'
                          }`}
                        >
                          <div className={`mb-1 flex items-center gap-1 text-[9px] font-semibold ${
                            isOutgoing ? 'text-white/75' : 'text-theme-muted'
                          }`}>
                            <Smartphone size={10} />
                            <span>{getSessionLabel(msg.session_id)}</span>
                          </div>

                          {/* Badge de emisor si es bot o agente */}
                          {isOutgoing && (
                            <div className="text-[9px] font-bold uppercase tracking-wider mb-1 opacity-80 flex items-center gap-1">
                              {isBot ? (
                                <>
                                  <Bot size={10} /> Respuesta Automática / IA
                                </>
                              ) : isAgent ? (
                                <>
                                  <User size={10} /> Operador en Vivo
                                </>
                              ) : (
                                'Enviado'
                              )}
                            </div>
                          )}

                          {/* Contenido del Mensaje */}
                          <div className="whitespace-pre-wrap break-words [overflow-wrap:anywhere]">
                            {msg.content}
                          </div>

                          {/* Hora y checks de lectura */}
                          <div
                            className={`text-[9px] mt-1 flex items-center justify-end gap-1 ${
                              isOutgoing ? 'text-white/80' : 'text-theme-muted'
                            }`}
                          >
                            <span>
                              {new Date(msg.created_at).toLocaleTimeString('es-ES', {
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </span>
                            {isOutgoing && <CheckCheck size={12} className="text-white/90" />}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Caja inferior de envío de mensaje */}
              <div className="min-w-0 p-2 sm:p-3 bg-theme-card border-t border-theme">
                <form onSubmit={handleSendMessage} className="flex min-w-0 items-center gap-2">
                  <input
                    type="text"
                    placeholder={`Escribe un mensaje para +${selectedContact.phone}...`}
                    value={inputText}
                    onChange={(e) => setInputText(e.target.value)}
                    disabled={isSending}
                    className="min-w-0 flex-1 px-3 sm:px-4 py-3 bg-theme-base border border-theme rounded-xl text-xs text-theme-main placeholder-theme-muted focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all"
                  />
                  <button
                    type="submit"
                    disabled={isSending || !inputText.trim()}
                    className="min-h-[44px] min-w-[44px] shrink-0 px-3 xl:px-5 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-xl text-xs font-bold flex items-center justify-center gap-2 shadow-md transition-all active:scale-95 disabled:opacity-50"
                  >
                    {isSending ? (
                      <RefreshCw size={15} className="animate-spin" />
                    ) : (
                      <Send size={15} />
                    )}
                    <span className="hidden xl:inline">Enviar</span>
                  </button>
                </form>
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-theme-muted p-8 text-center">
              <div className="w-16 h-16 rounded-full bg-theme-card border border-theme flex items-center justify-center mb-4 text-primary-500 shadow-sm">
                <Smartphone size={32} />
              </div>
              <h3 className="text-base font-bold text-theme-main mb-1">Centro de Conversaciones de WhatsApp</h3>
              <p className="text-xs max-w-sm">
                Selecciona una conversación de la columna izquierda o registra un nuevo contacto para ver su historial y chatear directamente.
              </p>
            </div>
          )}
        </div>

        {/* ========================================================
            COLUMNA 3: FICHA DE DETALLES DEL USUARIO (DERECHA)
            ======================================================== */}
        {selectedContact && isEditingContact && (
          <div className="absolute inset-0 z-20 w-full border-theme bg-theme-card flex flex-col overflow-y-auto animate-in slide-in-from-right duration-200 2xl:static 2xl:w-80 2xl:shrink-0 2xl:border-l">
            <div className="p-4 border-b border-theme flex items-center justify-between bg-theme-base/20">
              <h3 className="font-bold text-sm text-theme-main flex items-center gap-2">
                <User size={16} className="text-primary-500" />
                Ficha del Usuario
              </h3>
              <button
                onClick={() => setIsEditingContact(false)}
                className="p-1 hover:bg-theme-base text-theme-muted rounded-lg"
              >
                <X size={16} />
              </button>
            </div>

            <div className="p-5 space-y-4 text-xs">
              {/* Avatar Grande */}
              <div className="flex flex-col items-center text-center pb-2">
                <div className="w-16 h-16 rounded-full bg-gradient-to-tr from-primary-600 to-indigo-600 text-white flex items-center justify-center font-extrabold text-2xl shadow-lg mb-2">
                  {editForm.name ? editForm.name.charAt(0).toUpperCase() : <User size={24} />}
                </div>
                <h4 className="font-bold text-sm text-theme-main">{editForm.name || 'Sin nombre'}</h4>
                <p className="text-theme-muted font-mono">+{selectedContact.phone}</p>
              </div>

              {/* Nombre */}
              <div>
                <label className="block text-[11px] font-semibold text-theme-muted mb-1 flex items-center gap-1.5">
                  <User size={13} /> Nombre Completo / Apodo
                </label>
                <input
                  type="text"
                  value={editForm.name || ''}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  className="w-full px-3 py-2 bg-theme-base border border-theme rounded-xl text-xs text-theme-main focus:ring-2 focus:ring-primary-500 outline-none"
                  placeholder="Ej. Juan Pérez"
                />
              </div>

              {/* Correo Electrónico */}
              <div>
                <label className="block text-[11px] font-semibold text-theme-muted mb-1 flex items-center gap-1.5">
                  <Mail size={13} /> Correo Electrónico
                </label>
                <input
                  type="email"
                  value={editForm.email || ''}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  className="w-full px-3 py-2 bg-theme-base border border-theme rounded-xl text-xs text-theme-main focus:ring-2 focus:ring-primary-500 outline-none"
                  placeholder="ejemplo@correo.com"
                />
              </div>

              {/* País */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-[11px] font-semibold text-theme-muted mb-1 flex items-center gap-1.5">
                    <Globe size={13} /> Cód. País
                  </label>
                  <input
                    type="text"
                    value={editForm.country_code || ''}
                    onChange={(e) => setEditForm({ ...editForm, country_code: e.target.value })}
                    className="w-full px-3 py-2 bg-theme-base border border-theme rounded-xl text-xs text-theme-main focus:ring-2 focus:ring-primary-500 outline-none font-mono"
                    placeholder="51"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-theme-muted mb-1">
                    País
                  </label>
                  <input
                    type="text"
                    value={editForm.country_name || ''}
                    onChange={(e) => setEditForm({ ...editForm, country_name: e.target.value })}
                    className="w-full px-3 py-2 bg-theme-base border border-theme rounded-xl text-xs text-theme-main focus:ring-2 focus:ring-primary-500 outline-none"
                    placeholder="Perú"
                  />
                </div>
              </div>

              {/* Notas u Observaciones */}
              <div>
                <label className="block text-[11px] font-semibold text-theme-muted mb-1 flex items-center gap-1.5">
                  <FileText size={13} /> Notas / Observaciones Internas
                </label>
                <textarea
                  rows={3}
                  value={editForm.notes || ''}
                  onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                  className="w-full px-3 py-2 bg-theme-base border border-theme rounded-xl text-xs text-theme-main focus:ring-2 focus:ring-primary-500 outline-none resize-none"
                  placeholder="Detalles sobre compras, preferencias, cotizaciones, etc..."
                />
              </div>

              {/* Botón Guardar Cambios */}
              <div className="pt-2 flex flex-col gap-2">
                <button
                  onClick={handleSaveContactDetails}
                  disabled={isSavingContact}
                  className="w-full py-2.5 bg-primary-600 hover:bg-primary-700 text-white font-bold rounded-xl flex items-center justify-center gap-2 shadow-sm transition-all active:scale-95 disabled:opacity-50"
                >
                  {isSavingContact ? <RefreshCw size={14} className="animate-spin" /> : <Check size={14} />}
                  <span>Guardar Datos</span>
                </button>

                <button
                  onClick={() => setShowDeleteModal(true)}
                  className="w-full py-2 bg-red-500/10 hover:bg-red-500 text-red-600 hover:text-white font-bold rounded-xl flex items-center justify-center gap-2 border border-red-500/20 transition-all active:scale-95"
                >
                  <Trash2 size={14} />
                  <span>Eliminar Contacto</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal: Registrar Nuevo Contacto */}
      {showNewContactModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-theme-card rounded-2xl max-w-md w-full p-6 border border-theme shadow-2xl animate-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between pb-4 border-b border-theme">
              <h3 className="font-bold text-base text-theme-main flex items-center gap-2">
                <Plus size={18} className="text-primary-500" />
                Registrar Nuevo Contacto
              </h3>
              <button
                onClick={() => setShowNewContactModal(false)}
                className="p-1 hover:bg-theme-base text-theme-muted rounded-lg"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleCreateContact} className="space-y-3 mt-4 text-xs">
              <div>
                <label className="block font-semibold text-theme-muted mb-1">
                  Número Telefónico (con código de país) *
                </label>
                <input
                  type="text"
                  required
                  placeholder="Ej. 51976020013"
                  value={newContactForm.phone}
                  onChange={(e) => setNewContactForm({ ...newContactForm, phone: e.target.value })}
                  className="w-full px-3 py-2 bg-theme-base border border-theme rounded-xl text-xs text-theme-main focus:ring-2 focus:ring-primary-500 outline-none font-mono"
                />
              </div>

              <div>
                <label className="block font-semibold text-theme-muted mb-1">Nombre Completo</label>
                <input
                  type="text"
                  placeholder="Ej. Carlos Mendoza"
                  value={newContactForm.name}
                  onChange={(e) => setNewContactForm({ ...newContactForm, name: e.target.value })}
                  className="w-full px-3 py-2 bg-theme-base border border-theme rounded-xl text-xs text-theme-main focus:ring-2 focus:ring-primary-500 outline-none"
                />
              </div>

              <div>
                <label className="block font-semibold text-theme-muted mb-1">Correo Electrónico</label>
                <input
                  type="email"
                  placeholder="carlos@correo.com"
                  value={newContactForm.email}
                  onChange={(e) => setNewContactForm({ ...newContactForm, email: e.target.value })}
                  className="w-full px-3 py-2 bg-theme-base border border-theme rounded-xl text-xs text-theme-main focus:ring-2 focus:ring-primary-500 outline-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block font-semibold text-theme-muted mb-1">Cód. País</label>
                  <input
                    type="text"
                    placeholder="51"
                    value={newContactForm.country_code}
                    onChange={(e) => setNewContactForm({ ...newContactForm, country_code: e.target.value })}
                    className="w-full px-3 py-2 bg-theme-base border border-theme rounded-xl text-xs text-theme-main focus:ring-2 focus:ring-primary-500 outline-none font-mono"
                  />
                </div>
                <div>
                  <label className="block font-semibold text-theme-muted mb-1">Nombre País</label>
                  <input
                    type="text"
                    placeholder="Perú"
                    value={newContactForm.country_name}
                    onChange={(e) => setNewContactForm({ ...newContactForm, country_name: e.target.value })}
                    className="w-full px-3 py-2 bg-theme-base border border-theme rounded-xl text-xs text-theme-main focus:ring-2 focus:ring-primary-500 outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block font-semibold text-theme-muted mb-1">Notas Internas</label>
                <textarea
                  rows={2}
                  placeholder="Observaciones iniciales..."
                  value={newContactForm.notes}
                  onChange={(e) => setNewContactForm({ ...newContactForm, notes: e.target.value })}
                  className="w-full px-3 py-2 bg-theme-base border border-theme rounded-xl text-xs text-theme-main focus:ring-2 focus:ring-primary-500 outline-none resize-none"
                />
              </div>

              <div className="pt-3 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowNewContactModal(false)}
                  className="px-4 py-2 border border-theme rounded-xl text-theme-muted hover:bg-theme-base font-semibold"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isCreatingContact}
                  className="px-5 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-xl font-bold flex items-center gap-1.5 shadow-md disabled:opacity-50"
                >
                  {isCreatingContact && <RefreshCw size={14} className="animate-spin" />}
                  Guardar Contacto
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Confirmar Eliminación */}
      <ConfirmModal
        isOpen={showDeleteModal}
        isLoading={isDeletingContact}
        onClose={() => {
          if (!isDeletingContact) setShowDeleteModal(false);
        }}
        onConfirm={handleDeleteContact}
        title="¿Eliminar Contacto e Historial?"
        message={`¿Estás seguro de eliminar a "${selectedContact?.name || selectedContact?.phone}"? Se borrarán definitivamente todos los mensajes de su conversación en la base de datos PostgreSQL.`}
        confirmText={isDeletingContact ? "Eliminando..." : "Eliminar por Completo"}
        cancelText="Cancelar"
        type="danger"
      />
    </div>
  );
};
